import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { verifyPin, requestPin } from '../api/participantAuth';
import { getDeviceFingerprint } from '../utils/deviceFingerprint';
import BackToDashboardButton from '../components/BackToDashboardButton';

const PinEntryPage = () => {
  const history = useHistory();
  const location = useLocation();
  const [pinCode, setPinCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Get data passed from the login selection page
  const { participantId, participantName, deliveryMethod, contactInfo, verificationId, expiresIn } = location.state || {};

  // Redirect if no participant data
  React.useEffect(() => {
    if (!participantId) {
      history.replace('/race/select-login');
    }
  }, [participantId, history]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!pinCode || pinCode.length !== 4) {
      setError('Please enter the 4-digit PIN code.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Call the actual API to verify the PIN
      const deviceId = getDeviceFingerprint();
      const accountId = "7"; // CJC Account ID

      const result = await verifyPin(verificationId, pinCode, deviceId, accountId);
      

      if (result.success && result.token) {
        // Store auth info and redirect to dashboard
        
        // The API might return participant data directly or nested under participant
        const participantData = result.participant || result;
        
        const authData = {
          participantId: participantData.id || participantData.participantId || participantId,
          fullName: participantData.fullName || participantData.participantName || participantName,
          participantName: participantData.fullName || participantData.participantName || participantName, // Keep both for compatibility
          email: participantData.email || participantData.emailAddress,
          mobile: participantData.mobile,
          loginTime: new Date().toISOString(),
          expiresAt: new Date(Date.now() + (result.expiresIn * 1000)).toISOString(),
          authToken: {
            Token: result.token
          }
        };
        
        
        sessionStorage.setItem('participantAuth', JSON.stringify(authData));
        
        history.push('/race/dashboard');
      } else {
        setError(result.message || 'Invalid PIN code. Please try again.');
        setPinCode('');
      }

    } catch (err) {
      setError('Verification failed. Please try again.');
      setPinCode('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    setError('');
    
    try {
      // Call the API to resend the PIN
      const deviceId = getDeviceFingerprint();
      const accountId = "7";
      
      
      const result = await requestPin(participantId, deliveryMethod, deviceId, accountId);
      
      if (result.success) {
        setError('PIN code resent successfully!');
        // Update the verificationId for the new PIN
        if (result.verificationId && location.state) {
          location.state.verificationId = result.verificationId;
        }
      } else {
        setError(result.message || 'Failed to resend PIN code.');
      }
    } catch (err) {
      setError('Failed to resend PIN code. Please try again.');
    }
    
    // Clear success message after 3 seconds
    setTimeout(() => {
      if (error && error.includes('resent successfully')) {
        setError('');
      }
    }, 3000);
  };

  if (!participantId) {
    return null; // Will redirect in useEffect
  }

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-lg-5 col-md-7">
          <div className="card shadow">
            <div className="card-body p-5">
              {/* Header */}
              <div className="text-center mb-4">
                <div className="mb-3">
                  <i className="bi bi-shield-check text-success" style={{ fontSize: '3rem' }}></i>
                </div>
                <h2 className="fw-bold mb-2">Enter PIN Code</h2>
                <p className="text-muted">
                  We've sent a 4-digit PIN code to your {deliveryMethod}
                </p>
              </div>

              {/* Participant Info */}
              <div className="alert alert-info mb-4">
                <div className="d-flex align-items-center">
                  <i className="bi bi-person-circle me-2"></i>
                  <div>
                    <div className="fw-semibold">{participantName}</div>
                    <small className="text-muted">
                      PIN sent to: {contactInfo}
                    </small>
                  </div>
                </div>
              </div>

              {/* Error Alert */}
              {error && (
                <div className={`alert ${error.includes('resent') ? 'alert-success' : 'alert-danger'}`} role="alert">
                  <i className={`bi ${error.includes('resent') ? 'bi-check-circle' : 'bi-exclamation-triangle'} me-2`}></i>
                  {error}
                </div>
              )}

              {/* PIN Entry Form */}
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label htmlFor="pinCode" className="form-label fw-semibold">
                    4-Digit PIN Code
                  </label>
                  <input
                    type="text"
                    id="pinCode"
                    className="form-control form-control-lg text-center"
                    value={pinCode}
                    onChange={(e) => setPinCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    placeholder="••••"
                    maxLength="4"
                    required
                    autoFocus
                    style={{ 
                      fontSize: '2rem', 
                      letterSpacing: '0.5rem',
                      fontFamily: 'monospace'
                    }}
                  />
                  <div className="form-text text-center">
                    Enter the 4-digit code sent to your {deliveryMethod}
                  </div>
                </div>

                {/* Submit Button */}
                <div className="d-grid mb-3">
                  <button
                    type="submit"
                    className="btn btn-success btn-lg"
                    disabled={isSubmitting || pinCode.length !== 4}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Verifying...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-circle me-2"></i>
                        Verify PIN
                      </>
                    )}
                  </button>
                </div>

                {/* Resend PIN */}
                <div className="text-center mb-3">
                  <button
                    type="button"
                    className="btn btn-link"
                    onClick={handleResend}
                    disabled={isSubmitting}
                  >
                    <i className="bi bi-arrow-repeat me-1"></i>
                    Didn't receive it? Resend PIN
                  </button>
                </div>

                {/* Back Button */}
                <div className="text-center">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => history.goBack()}
                    disabled={isSubmitting}
                  >
                    <i className="bi bi-arrow-left me-1"></i>
                    Back
                  </button>
                </div>
              </form>

              {/* Help Text */}
              <div className="mt-4 pt-3 border-top text-center">
                <small className="text-muted">
                  <i className="bi bi-info-circle me-1"></i>
                  PIN codes are valid for 10 minutes and can only be used once
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <BackToDashboardButton />
    </div>
  );
};

export default PinEntryPage;
